import CustomTooltip from '../../../shared/CustomTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Box,
    Button,
    Grid,
    InputLabel,
    List,
    ListItem,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAddIndividual7DayAdminMutation,
    useAddIndividualContractorUsersMutation,
    useAddIndividualGuestUsersMutation,
    useAddIndividualOwnerUsersMutation,
    useGetTeamListQuery,
    useGetUserListQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { capitalizeName } from '../../../../shared/utils';
import toast from '../../../shared/Toast';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import FormTextField from '../../../shared/FormTextField';
import FormSelect from '../../../shared/FormSelect';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { Add } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ProfileSectionCard from '../../Profile/ProfileSectionCard';
import { personalAccessTooltipContent } from '../../Profile/ProfileReadOnlyField';
import {
    AdminRole,
    ContractorRole,
    GuestRole,
    OwnerRole,
} from '../../../enums/users';
import { User } from '../../../types/user';
import { Schema } from './schema';

export default function NewUserForm() {
    const loggedInUser = UserRole();
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');

    type FormValues = z.infer<typeof Schema>;
    const methods = useForm<FormValues>({
        defaultValues: {
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            role: undefined,
            isActive: true,
            department: null,
            vehiclePlate: '',
        },
        resolver: zodResolver(Schema),
    });

    const {
        control,
        handleSubmit,
        setError,
        formState: { isDirty },
    } = methods;

    const url = loggedInUser?.role.includes('owner')
        ? `/owners/${loggedInUser?.company}/users`
        : loggedInUser?.role.includes('guest')
          ? `/guests/${loggedInUser?.company}/users`
          : loggedInUser?.role.includes('contractor')
            ? `/contractors/${loggedInUser?.company}/users`
            : `/7-day/users`;

    const { data: getUsers } = useGetUserListQuery(url, {
        skip: !loggedInUser?.company,
    });

    const [addNewOwnerUser] = useAddIndividualOwnerUsersMutation();
    const [addNewGuestUser] = useAddIndividualGuestUsersMutation();
    const [addNewContractorUser] = useAddIndividualContractorUsersMutation();
    const [addNew7DayAdmin] = useAddIndividual7DayAdminMutation();
    const { data: teams } = useGetTeamListQuery(loggedInUser?.company);

    const values = useWatch({ control });

    const handleDuplicateEmail = (data: FormValues) => {
        const duplicate = getUsers?.find(
            (user: User) => user.email === data.email,
        );

        if (duplicate) {
            setError('email', {
                message: 'A user with this email already exists',
            });
            return true;
        }

        return false;
    };

    const navigate = useNavigate();

    const RoleOptions =
        loggedInUser?.role === 'admin'
            ? [{ value: 'admin', label: 'Admin' }]
            : loggedInUser.role.includes('guest')
              ? loggedInUser?.role === 'guest_department_admin'
                  ? [
                        {
                            value: 'user',
                            label: 'User',
                            isDefault: true,
                        },
                        {
                            value: 'viewOnlyUser',
                            label: 'View Only User',
                        },
                    ]
                  : [
                        {
                            value: 'admin',
                            label: 'Company Admin',
                        },
                        {
                            value: 'teamAdmin',
                            label: 'Team Admin',
                        },
                        {
                            value: 'user',
                            label: 'User',
                            isDefault: true,
                        },
                        {
                            value: 'viewOnlyUser',
                            label: 'View Only User',
                        },
                    ]
              : loggedInUser?.role.includes('owner')
                ? [
                      {
                          value: 'admin',
                          label: 'Admin',
                      },
                      {
                          value: 'user',
                          label: 'User',
                          isDefault: true,
                      },
                      {
                          value: 'viewOnlyUser',
                          label: 'View Only User',
                      },
                  ]
                : loggedInUser?.role.includes('contractor')
                  ? [
                        {
                            value: 'admin',
                            label: 'Admin',
                        },
                        {
                            value: 'user',
                            label: 'User',
                        },
                    ]
                  : [];

    const onSubmit = async (data: FormValues) => {
        if (handleDuplicateEmail(data)) return;

        const { role, ...rest } = data;
        let response: any;

        try {
            if (loggedInUser?.role.includes('guest')) {
                const request = {
                    ...rest,
                    role:
                        role === 'admin'
                            ? GuestRole.Admin
                            : role === 'teamAdmin'
                              ? GuestRole.DepartmentAdmin
                              : GuestRole.User,
                    viewOnly: role === 'viewOnlyUser',
                };

                response = await addNewGuestUser([
                    loggedInUser?.company,
                    request,
                ]).unwrap();
            } else if (loggedInUser?.role.includes('owner')) {
                const request = {
                    ...rest,
                    role: role === 'admin' ? OwnerRole.Admin : OwnerRole.User,
                    viewOnly: role === 'viewOnlyUser',
                };

                response = await addNewOwnerUser([
                    loggedInUser?.company,
                    request,
                ]).unwrap();
            } else if (loggedInUser?.role.includes('contractor')) {
                const request = {
                    ...rest,
                    role:
                        role === 'admin'
                            ? ContractorRole.Admin
                            : ContractorRole.User,
                };

                response = await addNewContractorUser([
                    loggedInUser?.company,
                    request,
                ]).unwrap();
            } else if (loggedInUser?.role === AdminRole.Admin) {
                response = await addNew7DayAdmin({ ...rest }).unwrap();
            }

            toast.success('User created successfully');

            navigate(
                `/users/${response.firstName}/${response.id}`,
                response.isActive
                    ? {
                          state: {
                              name: 'newlyAddedUser',
                          },
                      }
                    : undefined,
            );
        } catch (error: any) {
            if (
                error.data?.error.includes('duplicate') ||
                error.data?.message?.includes('already registered')
            ) {
                toast.error(
                    'Users email address exists under another company profile.',
                );
                return;
            }

            if (error.data?.error.includes('users.invalid_domain')) {
                toast.error(`${error.data?.message}`);
                return;
            }
            toast.error('Error creating new user.');
        }
    };

    const noopEdit = () => {};

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3} sx={{ width: '100%' }}>
                    <ProfileSectionCard
                        icon={<PersonIcon sx={{ fontSize: 22 }} />}
                        title='Personal Details'
                        subtitle='Name, contact, access level, and account status for the new user.'
                        editButtonLabel='Edit Personal Details'
                        onEdit={noopEdit}
                        showEditButton={false}
                    >
                        <Grid container columnSpacing='50px' rowSpacing='10px'>
                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <InputLabel
                                        required={values.firstName === ''}
                                    >
                                        First Name{' '}
                                    </InputLabel>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormTextField
                                        name='firstName'
                                        control={control}
                                        placeholder='John'
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <InputLabel
                                        required={values.lastName === ''}
                                    >
                                        Surname
                                    </InputLabel>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormTextField
                                        name='lastName'
                                        control={control}
                                        placeholder='Smith'
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <InputLabel required={values.phone === ''}>
                                        Phone
                                    </InputLabel>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormTextField
                                        name='phone'
                                        control={control}
                                        placeholder='0414789456'
                                        type='tel'
                                    />
                                </Grid>
                            </Grid>
                            {loggedInUser?.role.includes('guest') && (
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel
                                            required={
                                                values.role === 'teamAdmin' &&
                                                !values.department?.id
                                            }
                                        >
                                            Team
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        <List>
                                                            By selecting* the
                                                            users team, they
                                                            will appear in the
                                                            'Team Managers'
                                                            profile. Team
                                                            Managers will have
                                                            access to view and
                                                            manage users that
                                                            belong to their team
                                                            as well as view and
                                                            edit any
                                                            reservations that
                                                            belong to them.
                                                        </List>
                                                        <List>
                                                            * Only a company
                                                            admin can change a
                                                            users Team.
                                                        </List>
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: '16px',
                                                    }}
                                                />
                                            </CustomTooltip>
                                        </InputLabel>
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        {loggedInUser?.role ===
                                        'guest_department_admin' ? (
                                            <Typography
                                                variant='body2'
                                                sx={{ color: 'text.primary' }}
                                            >
                                                This user will be assigned the
                                                same Team as you.
                                            </Typography>
                                        ) : (
                                            <FormAutocomplete
                                                name='department.id'
                                                control={control}
                                                options={
                                                    teams?.map((team) => ({
                                                        value: team.id,
                                                        label: capitalizeName(
                                                            team.name,
                                                        ),
                                                    })) ?? []
                                                }
                                                disableClearable={undefined}
                                                PaperComponent={({
                                                    children,
                                                }) => {
                                                    return (
                                                        <Paper>
                                                            <Button
                                                                fullWidth
                                                                size='large'
                                                                sx={{
                                                                    justifyContent:
                                                                        'flex-start',
                                                                    pl: 2,
                                                                    mt: 1,
                                                                }}
                                                                startIcon={
                                                                    <Add />
                                                                }
                                                                onMouseDown={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    window.open(
                                                                        '/newTeam',
                                                                        '_blank',
                                                                    );
                                                                }}
                                                            >
                                                                Create New Team
                                                            </Button>
                                                            {children}
                                                        </Paper>
                                                    );
                                                }}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <Stack
                                        direction='row'
                                        sx={{
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <InputLabel>Access</InputLabel>
                                        <CustomTooltip
                                            title={personalAccessTooltipContent(
                                                loggedInUser,
                                            )}
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormSelect
                                        name='role'
                                        control={control}
                                        options={RoleOptions}
                                        disabled={
                                            loggedInUser?.role ===
                                            AdminRole.Admin
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <Stack
                                        direction='row'
                                        sx={{
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <InputLabel>Status</InputLabel>
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    <List>
                                                        <ListItem>
                                                            {' '}
                                                            Enabled: Users can
                                                            access the site and
                                                            perform actions
                                                            based on their level
                                                            of access
                                                        </ListItem>
                                                        <ListItem>
                                                            {' '}
                                                            Disabled: Users will
                                                            not be able to
                                                            access the site
                                                        </ListItem>
                                                    </List>
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormSelect
                                        name='isActive'
                                        control={control}
                                        options={[
                                            {
                                                value: true,
                                                label: 'Enabled',
                                            },
                                            {
                                                value: false,
                                                label: 'Disabled',
                                            },
                                        ]}
                                    />
                                </Grid>
                            </Grid>

                            {loggedInUser?.role !== AdminRole.Admin && (
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel>
                                            Vehicle Plate{' '}
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        Some experiences might
                                                        need plate numbers to
                                                        give vehicles access to
                                                        the location.
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: '16px',
                                                    }}
                                                />
                                            </CustomTooltip>
                                        </InputLabel>
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        <FormTextField
                                            name='vehiclePlate'
                                            control={control}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </ProfileSectionCard>

                    <ProfileSectionCard
                        icon={<ShieldOutlinedIcon sx={{ fontSize: 22 }} />}
                        title='Login & Security'
                        subtitle='Email address used to create the account and send the invitation.'
                        editButtonLabel='Edit Login & Security'
                        onEdit={noopEdit}
                        showEditButton={false}
                    >
                        <Grid container columnSpacing='50px' rowSpacing='10px'>
                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <InputLabel required={values.email === ''}>
                                        Email
                                    </InputLabel>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormTextField
                                        name='email'
                                        control={control}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </ProfileSectionCard>
                </Stack>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent:
                            isMobile || isIpad ? 'flex-end' : 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        py: { xs: 3, sm: 2 },
                        pt: { xs: 2, sm: 3 },
                    }}
                >
                    {!(isMobile || isIpad) && (
                        <Button
                            type='button'
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(`/users`)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Back to users
                        </Button>
                    )}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ ml: isMobile || isIpad ? 0 : 'auto' }}
                    >
                        {isDirty ? (
                            <Typography
                                variant='body2'
                                sx={{ color: 'text.primary' }}
                            >
                                You have unsaved changes
                            </Typography>
                        ) : null}
                        <Button variant='contained' type='submit'>
                            Save
                        </Button>
                    </Stack>
                </Box>
            </form>
        </FormProvider>
    );
}
